import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check, X, Pin, Pause, Mail, Download, RefreshCw, CheckCircle2, BookOpen, Pencil } from "lucide-react";

const STATUS_COLOR = {
  pending_review: "bg-[#D4A373]/20 text-[#8b6a3f]",
  trial: "bg-[#0F766E]/10 text-[#0F766E]",
  active: "bg-[#0F766E]/10 text-[#0F766E]",
  rejected: "bg-[#9D5C5C]/10 text-[#9D5C5C]",
  suspended: "bg-[#5F6C60]/15 text-[#2C352D]",
};

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");

  const loadStats = () => api.get("/admin/stats").then(r => setStats(r.data));
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Owner console</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">Admin</h1>
        </div>
        <Link to="/owner-guide" data-testid="open-owner-guide">
          <Button variant="outline" className="rounded-full mt-2 text-[#0F766E] border-[#0F766E]/40 hover:bg-[#0F766E]/10">
            <BookOpen className="w-4 h-4 mr-2" strokeWidth={1.5} /> Owner's Guide
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Stat label="Pending review" value={stats?.pending ?? "—"} tone="warn" />
        <Stat label="Trial deals" value={stats?.trial ?? "—"} />
        <Stat label="Active subs" value={stats?.active ?? "—"} />
        <Stat label="Last 30d revenue" value={stats ? `$${stats.revenue_last_30d.toFixed(2)}` : "—"} accent />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-white border border-[#E8E2D6] rounded-full p-1">
          <TabsTrigger data-testid="admin-tab-pending" value="pending" className="rounded-full">Pending</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-all" value="all" className="rounded-full">All deals</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-full">Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-tx" value="tx" className="rounded-full">Transactions</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-contact" value="contact" className="rounded-full">Contact</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6"><DealsList status="pending_review" onChange={loadStats} /></TabsContent>
        <TabsContent value="all" className="mt-6"><DealsList status={null} onChange={loadStats} /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersList /></TabsContent>
        <TabsContent value="tx" className="mt-6"><TransactionsList /></TabsContent>
        <TabsContent value="contact" className="mt-6"><ContactMessages /></TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, tone, accent }) {
  return (
    <div className={`bg-white rounded-2xl border border-[#E8E2D6] p-5 ${tone === "warn" ? "ring-1 ring-[#D4A373]/40" : ""}`}>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">{label}</div>
      <div className={`font-serif-display text-3xl mt-2 ${accent ? "text-[#0F766E]" : ""}`}>{value}</div>
    </div>
  );
}

function DealsList({ status, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const url = status ? `/admin/deals?status=${status}` : `/admin/deals`;
    api.get(url).then(r => setItems(r.data.items)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    await api.post(`/admin/deals/${id}/approve`);
    toast.success("Approved — trial started (3 days)");
    load(); onChange?.();
  };
  const reject = async (id, reason) => {
    await api.post(`/admin/deals/${id}/reject`, { reason });
    toast.success("Rejected");
    load(); onChange?.();
  };
  const suspend = async (id) => {
    if (!window.confirm("Suspend this deal?")) return;
    await api.post(`/admin/deals/${id}/suspend`);
    toast.success("Suspended");
    load(); onChange?.();
  };
  const feature = async (id) => {
    await api.post(`/admin/deals/${id}/feature`, { days: 7 });
    toast.success("Featured for 7 days");
    load();
  };

  const permaFree = async (id, on) => {
    if (on && !window.confirm("Make this deal permanently live and free (no subscription required)?")) return;
    if (on) await api.post(`/admin/deals/${id}/perma-free`);
    else await api.delete(`/admin/deals/${id}/perma-free`);
    toast.success(on ? "Now permanently free" : "Perma-free revoked");
    load(); onChange?.();
  };

  if (loading) return <div className="text-[#5F6C60]">Loading…</div>;
  if (items.length === 0) return <div className="bg-white rounded-2xl border border-[#E8E2D6] p-10 text-center font-serif-display text-2xl text-[#5F6C60]">Nothing here</div>;

  return (
    <ul className="bg-white rounded-2xl border border-[#E8E2D6] divide-y divide-[#E8E2D6]">
      {items.map(d => (
        <li key={d.id} data-testid={`admin-deal-${d.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLOR[d.status] || "bg-[#E8E2D6] text-[#2C352D]"}`}>{d.status}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#E8E2D6] text-[#2C352D]">{d.category}</span>
              {d.featured_until && new Date(d.featured_until) > new Date() && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#D97757] text-white">Featured</span>}
              {d.perma_free && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#0F766E] text-white">Free forever</span>}
            </div>
            <div className="font-serif-display text-lg">{d.title}</div>
            <div className="text-sm text-[#5F6C60]">{d.owner_name} ({d.owner_role}) · {d.city}, {d.state} · ${d.deal_price.toFixed(0)} · {d.duration_minutes} min</div>
            {d.rejection_reason && <div className="text-xs text-[#9D5C5C] mt-1">Rejected: {d.rejection_reason}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            {d.status === "pending_review" && (
              <>
                <Button data-testid={`approve-${d.id}`} size="sm" onClick={()=>approve(d.id)} className="rounded-full bg-[#0F766E] hover:bg-[#0C5F58] text-white"><Check className="w-3.5 h-3.5 mr-1.5" /> Approve</Button>
                <RejectDialog onConfirm={(reason)=>reject(d.id, reason)} />
              </>
            )}
            {(d.status === "trial" || d.status === "active") && (
              <Button size="sm" variant="outline" onClick={()=>feature(d.id)} className="rounded-full"><Pin className="w-3.5 h-3.5 mr-1.5" /> Feature 7d</Button>
            )}
            {!d.perma_free && d.status !== "pending_review" && (
              <Button data-testid={`perma-free-${d.id}`} size="sm" variant="outline" onClick={()=>permaFree(d.id, true)} className="rounded-full text-[#0F766E] border-[#0F766E]/40 hover:bg-[#0F766E]/10">Free forever</Button>
            )}
            {d.perma_free && (
              <Button data-testid={`revoke-free-${d.id}`} size="sm" variant="outline" onClick={()=>permaFree(d.id, false)} className="rounded-full">Revoke free</Button>
            )}
            {d.status !== "suspended" && (
              <Button size="sm" variant="outline" onClick={()=>suspend(d.id)} className="rounded-full"><Pause className="w-3.5 h-3.5 mr-1.5" /> Suspend</Button>
            )}
            <Link to={`/dashboard/edit/${d.id}`}>
              <Button data-testid={`admin-edit-${d.id}`} size="sm" variant="outline" className="rounded-full"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RejectDialog({ onConfirm }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="reject-open" size="sm" variant="outline" className="rounded-full text-[#9D5C5C] border-[#9D5C5C]/40 hover:bg-[#9D5C5C]/10"><X className="w-3.5 h-3.5 mr-1.5" /> Reject</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Reject deal</DialogTitle></DialogHeader>
        <Textarea data-testid="reject-reason" value={reason} onChange={e=>setReason(e.target.value)} rows={4} placeholder="Reason (visible to the therapist)" />
        <DialogFooter>
          <Button data-testid="reject-confirm" onClick={()=>{ if(reason.trim().length<3){ toast.error("Reason too short"); return;} onConfirm(reason); setOpen(false); setReason(""); }} className="rounded-full bg-[#9D5C5C] hover:bg-[#7d4848] text-white">Send rejection</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/users").then(r => setItems(r.data.items)); }, []);
  const suspend = async (id) => {
    if (!window.confirm("Suspend user + all their deals?")) return;
    await api.post(`/admin/users/${id}/suspend`);
    toast.success("Suspended");
    api.get("/admin/users").then(r => setItems(r.data.items));
  };
  const sendMessage = async (id) => {
    const msg = window.prompt("Message to send:");
    if (!msg) return;
    await api.post("/admin/messages", { user_id: id, message: msg });
    toast.success("Sent");
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden">
      <div className="flex items-center justify-end p-3 border-b border-[#E8E2D6]">
        <a data-testid="export-users" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/users`} className="text-xs flex items-center gap-1 text-[#5F6C60] hover:text-[#D97757]"><Download className="w-3.5 h-3.5" /> Export CSV</a>
      </div>
      <ul className="divide-y divide-[#E8E2D6]">
        {items.map(u => (
          <li key={u.id} data-testid={`admin-user-${u.id}`} className="p-4 flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">{u.name} <span className="text-xs text-[#5F6C60]">· {u.role}</span> {u.suspended && <span className="text-xs text-[#9D5C5C] ml-1">(suspended)</span>}</div>
              <div className="text-xs text-[#5F6C60]">{u.email} · joined {u.created_at?.slice(0,10)}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={()=>sendMessage(u.id)}><Mail className="w-3.5 h-3.5 mr-1.5" /> Message</Button>
              {u.role !== "admin" && !u.suspended && <Button size="sm" variant="outline" className="rounded-full" onClick={()=>suspend(u.id)}><Pause className="w-3.5 h-3.5 mr-1.5" /> Suspend</Button>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactMessages() {
  const [items, setItems] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const load = () => {
    const url = showResolved ? "/admin/contact-messages" : "/admin/contact-messages?resolved=false";
    api.get(url).then(r => setItems(r.data.items));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showResolved]);

  const markResolved = async (id) => {
    await api.post(`/admin/contact-messages/${id}/resolved`);
    toast.success("Marked resolved");
    load();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-[#E8E2D6]">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">
          {showResolved ? "All messages" : "Open messages"}
        </div>
        <button
          data-testid="toggle-resolved"
          onClick={() => setShowResolved(s => !s)}
          className="text-xs text-[#5F6C60] hover:text-[#D97757]"
        >
          {showResolved ? "Show open only" : "Show all"}
        </button>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-[#5F6C60]">
          {showResolved ? "No messages yet" : "No open messages — all caught up!"}
        </div>
      ) : (
        <ul className="divide-y divide-[#E8E2D6]">
          {items.map(m => (
            <li key={m.id} data-testid={`contact-msg-${m.id}`} className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#2C352D]">{m.name}</span>
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="text-sm text-[#5F6C60] hover:text-[#D97757]">{m.email}</a>
                    {m.resolved && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#0F766E]/10 text-[#0F766E] px-2 py-0.5 rounded-full">Resolved</span>}
                  </div>
                  <div className="font-serif-display text-lg mt-1">{m.subject}</div>
                  <p className="text-sm text-[#2C352D] mt-2 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  <div className="text-xs text-[#5F6C60] mt-3">{m.created_at?.slice(0,16).replace("T", " ")}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}>
                    <Button size="sm" variant="outline" className="rounded-full w-full"><Mail className="w-3.5 h-3.5 mr-1.5" /> Reply</Button>
                  </a>
                  {!m.resolved && (
                    <Button data-testid={`resolve-${m.id}`} size="sm" variant="outline" className="rounded-full text-[#0F766E] border-[#0F766E]/40 hover:bg-[#0F766E]/10" onClick={() => markResolved(m.id)}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Resolve
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function TransactionsList() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/transactions").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const refund = async (sid) => {
    if (!window.confirm("Mark refunded? Actual refund must be issued in Stripe Dashboard.")) return;
    await api.post(`/admin/transactions/${sid}/refund`);
    toast.success("Marked refunded");
    load();
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden">
      <div className="flex items-center justify-end p-3 border-b border-[#E8E2D6]">
        <a data-testid="export-tx" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/transactions`} className="text-xs flex items-center gap-1 text-[#5F6C60] hover:text-[#D97757]"><Download className="w-3.5 h-3.5" /> Export CSV</a>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-[#5F6C60]">No transactions yet</div>
      ) : (
        <ul className="divide-y divide-[#E8E2D6]">
          {items.map(t => (
            <li key={t.id} data-testid={`admin-tx-${t.id}`} className="p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium">${Number(t.amount).toFixed(2)} <span className="text-xs text-[#5F6C60]">{t.currency?.toUpperCase()} · {t.plan}</span></div>
                <div className="text-xs text-[#5F6C60]">{t.payment_status} · session {t.session_id?.slice(0,16)}… · {t.created_at?.slice(0,16)}</div>
              </div>
              {t.payment_status === "paid" && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={()=>refund(t.session_id)}><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refund</Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
